import { useLocation } from 'react-router-dom'

import { Box, Text, useBreakpoint } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0AvatarAlert } from '@factorialco/f0-react'
import { Share } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import LeftSection from 'modules/upsell/components/SelfUpsell/CheckoutModal/LeftSection'
import { getHelpCenterLocalizedLink } from 'modules/upsell/lib/helpCenterLinks'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type SuccessBoxProps = {
  moduleName: string
}

const SuccessBox = ({ moduleName }: SuccessBoxProps) => {
  const helpCenterLink = getHelpCenterLocalizedLink(moduleName)
  const { pathname } = useLocation()

  const trackEvent = () => {
    track(trackingEvents.upsell.openSetupGuide, {
      module: moduleName,
      from: 'self_upsell_modal',
      origin: pathname,
    })
  }

  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey300',
        },
      }}
      borderRadius={{ all: 'abs016' }}
      background="grey100"
      padding="s8"
      paddingBottom="s4"
      gap="s4"
      style={{
        flex: '1',
      }}
    >
      <Box
        alignItems="center"
        borderRadius={{ all: 'abs012' }}
        background="grey000White"
        boxShadow="s50"
        padding="s20"
        gap="s16"
      >
        <F0AvatarAlert type="positive" size="lg" />
        <Box textAlign="center" gap="s2">
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {i18n.t('self_upsell.success_step.payment_completed')}
          </Text.Normal>
          <Text.Normal size="200" color="grey700">
            {i18n.t('self_upsell.success_step.description')}
          </Text.Normal>
        </Box>
        <F0Button
          icon={Share}
          label={i18n.t('self_upsell.success_step.setup_guide')}
          variant="neutral"
          onClick={() => {
            trackEvent()
            window.open(helpCenterLink, '_blank')
          }}
        />
      </Box>
    </Box>
  )
}

type SuccessStepProps = {
  addon: Plan
  step: string
  onClose: () => void
  moduleName: AvailableModules
}

const SuccessStep = ({ addon, step, onClose, moduleName }: SuccessStepProps) => {
  const { configPlan } = useSelfUpsellContext()
  const { isMobile } = useBreakpoint()

  const handleClose = () => {
    onClose()
    window.location.reload()
  }

  useEffect(() => {
    track(trackingEvents.upsell.viewSelfUpsellSuccessStep, {
      module: moduleName,
    })
  }, [moduleName])

  return (
    <Box flexDirection="row" height="full">
      {!isMobile && (
        <LeftSection addon={addon} variant={configPlan.variant} step={step} iconName={moduleName} />
      )}
      <Box
        padding="s24"
        justifyContent="spaceBetween"
        style={{
          width: isMobile ? '100%' : '60%',
        }}
      >
        <Box
          gap="s16"
          style={{
            height: '100%',
          }}
        >
          <Box height="s24" />
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('self_upsell.success_step.all_set')}
          </Text.Normal>
          <SuccessBox moduleName={moduleName} />
        </Box>
        <Box flexDirection="row" gap="s8" flexWrap="wrap" justifyContent="flexEnd" paddingTop="s24">
          <F0Button
            size="lg"
            variant="outline"
            label={i18n.t('self_upsell.success_step.close')}
            onClick={handleClose}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default SuccessStep
